import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { PointIcon } from './icons/PointIcon';
import { PointInfoBubble } from './PointInfoBubble';
import { ChevronLeft, ChevronRight, X, Info as InfoIcon, MapPin, Users, AlertTriangle, Search, Music } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { createRoot } from 'react-dom/client';
import { APIProvider, useMapsLibrary, useApiLoadingStatus, APILoadingStatus } from '@vis.gl/react-google-maps';
import { getActiveDates } from '../store/useStore';
import { buildHighlight, boundsOf, type Bbox, type HighlightGeometry } from '../lib/placeHighlight';

// Use environment variable for token with user's provided token as fallback
const MAPBOX_TOKEN = (import.meta as any).env.VITE_MAPBOX_ACCESS_TOKEN || '';
mapboxgl.accessToken = MAPBOX_TOKEN;

const getGoogleApiKey = () => {
  if (typeof import.meta !== 'undefined' && import.meta.env && (import.meta as any).env.VITE_GOOGLE_MAPS_PLATFORM_KEY) {
    return (import.meta as any).env.VITE_GOOGLE_MAPS_PLATFORM_KEY;
  }
  try {
    const key = process.env.GOOGLE_MAPS_PLATFORM_KEY;
    if (key) return key;
  } catch (e) {}
  return '';
};
const GOOGLE_API_KEY = getGoogleApiKey();
const hasGoogleKey = Boolean(GOOGLE_API_KEY) && GOOGLE_API_KEY !== 'YOUR_API_KEY';

interface MapViewMapboxProps {
  isTeamView?: boolean;
  isEmbedView?: boolean; // Read-only mode: hide search, sidebars, point editing
  embedPointColor?: string;  // custom color for normal point icons in embed
  embedStageColor?: string;  // custom color for stage point icons in embed
}

export function MapViewMapbox({ isTeamView = false, isEmbedView = false, embedPointColor, embedStageColor }: MapViewMapboxProps) {
  if (hasGoogleKey) {
    return (
      <APIProvider apiKey={GOOGLE_API_KEY} version="weekly" libraries={['places', 'geocoding']}>
        <MapViewMapboxInner isTeamView={isTeamView} isEmbedView={isEmbedView} embedPointColor={embedPointColor} embedStageColor={embedStageColor} />
      </APIProvider>
    );
  }
  return <MapViewMapboxInner isTeamView={isTeamView} isEmbedView={isEmbedView} embedPointColor={embedPointColor} embedStageColor={embedStageColor} />;
}

function MapViewMapboxInner({ isTeamView = false, isEmbedView = false, embedPointColor, embedStageColor }: MapViewMapboxProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<Record<string, mapboxgl.Marker>>({});
  /** Épingle du lieu recherché, distincte des marqueurs de points opérationnels. */
  const searchMarkerRef = useRef<mapboxgl.Marker | null>(null);
  /** Dernier contour calculé — réappliqué à chaque rechargement du style de carte. */
  const highlightDataRef = useRef<HighlightGeometry | null>(null);
  
  const { points, allPoints, updatePoint, mapFocus, setMapFocus, currentUser, shifts: _shifts, issues, team, impersonatedTeamId, categories, addPoint, addShift, addIssue, effectiveCategoryId, isEffectiveManager } = useVisibleStore();
  const effectiveTeamId = impersonatedTeamId || currentUser.teamId;
  const isTeam = isTeamView || currentUser.role === 'staff';
  
  // Restricted access visibility filter: restricted points are only visible to team who have a shift assigned to them there.
  const visiblePoints = points.filter(p => {
    if ((isTeamView || currentUser.role === 'staff') && p.isRestricted) {
      return _shifts.some(s => s.teamId === effectiveTeamId && s.pointId === p.id);
    }
    return true;
  });

  // Focus map on the first upcoming shift point for team/artists
  useEffect(() => {
    if (isTeam && effectiveTeamId) {
      const targetTeam = team.find(st => st.id === effectiveTeamId);
      // Include parent artist shifts for sub-members
      const myShifts = _shifts.filter(s => s.teamId === effectiveTeamId || (targetTeam?.parentArtistId && s.teamId === targetTeam.parentArtistId));
      const isArtist = targetTeam?.isArtist === true;
      const visiblePointIds = new Set(visiblePoints.map(p => p.id));

      const filteredMyShifts = myShifts.filter(s => {
        const pt = (allPoints || points).find(p => p.id === s.pointId);
        if (!pt || !pt.location) return false;
        if (!visiblePointIds.has(pt.id)) return false;
        if (isArtist && !pt.isStage) return false;
        return true;
      });

      // Find earliest upcoming (or most recent past) shift
      const upcoming = filteredMyShifts
        .filter(s => new Date(s.end) > new Date())
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
      const past = filteredMyShifts
        .filter(s => new Date(s.end) <= new Date())
        .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());
      const firstShift = upcoming[0] || past[0];

      if (firstShift) {
        const pt = (allPoints || points).find(p => p.id === firstShift.pointId && p.location);
        if (pt && pt.location) {
          // Only refocus if not already focused on the FIRST upcoming shift point
          const isAlreadyOnFirst = mapFocus
            && Math.abs(pt.location.lat - mapFocus.lat) < 0.0001
            && Math.abs(pt.location.lng - mapFocus.lng) < 0.0001;
          if (!isAlreadyOnFirst) {
            setMapFocus({ lat: pt.location!.lat, lng: pt.location!.lng, zoomLevel: 18 });
          }
        }
      }
    }
  }, [effectiveTeamId, isTeamView, currentUser.role, _shifts, points, allPoints, team]);

  const [selectedPointId, setSelectedPointId] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFocusDrawerOpen, setIsFocusDrawerOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [descriptionModal, setDescriptionModal] = useState<{ name: string; markdown: string } | null>(null);
  const [pointSearchQuery, setPointSearchQuery] = useState('');

  // Custom Create Point & Issue Reporting Modals State
  const [createPointModal, setCreatePointModal] = useState<{ lat: number; lng: number } | null>(null);
  const [reportIssueModal, setReportIssueModal] = useState<{ lat: number; lng: number } | null>(null);

  const festivalDates = getActiveDates(points);
  const defaultCategory = effectiveCategoryId ? effectiveCategoryId : (categories[0]?.id || '');
  const isArtistCategoryManager = effectiveCategoryId && categories.find(c => c.id === effectiveCategoryId)?.isArtistCategory === true;
  const canSeeStageOption = currentUser.role === 'admin' || isArtistCategoryManager;

  const [newPointForm, setNewPointForm] = useState({
    name: '',
    categoryId: defaultCategory,
    isStage: false,
    date: festivalDates[0] || new Date().toISOString().split('T')[0],
    startTime: '08:00',
    endTime: '20:00',
    assignedTeam: [] as { teamId: string; start: string; end: string }[]
  });

  const [issueForm, setIssueForm] = useState({
    message: '',
    pointId: ''
  });

  const getNearestPoint = (lat: number, lng: number) => {
    if (points.length === 0) return '';
    let minDistance = Infinity;
    let nearestId = '';
    points.forEach(p => {
      if (!p.location) return;
      const dist = Math.pow(p.location.lat - lat, 2) + Math.pow(p.location.lng - lng, 2);
      if (dist < minDistance) {
        minDistance = dist;
        nearestId = p.id;
      }
    });
    return nearestId || points[0].id;
  };

  const handleLongPressOrRightClick = (lngLat: mapboxgl.LngLat) => {
    const { lat, lng } = lngLat;
    if (currentUser.role === 'admin' || isEffectiveManager) {
      const activeCatId = effectiveCategoryId ? effectiveCategoryId : (categories[0]?.id || '');
      const activeCatObj = categories.find(c => c.id === activeCatId);
      setNewPointForm({
        name: '',
        categoryId: activeCatId,
        isStage: activeCatObj?.isArtistCategory === true,
        date: festivalDates[0] || new Date().toISOString().split('T')[0],
        startTime: '08:00',
        endTime: '20:00',
        assignedTeam: []
      });
      setCreatePointModal({ lat, lng });
    } else {
      const nearestPtId = getNearestPoint(lat, lng);
      setIssueForm({
        message: '',
        pointId: nearestPtId
      });
      setReportIssueModal({ lat, lng });
    }
  };

  const handleLongPressOrRightClickRef = useRef(handleLongPressOrRightClick);
  useEffect(() => {
    handleLongPressOrRightClickRef.current = handleLongPressOrRightClick;
  });
  
  // Auto Style Logic (Day: 7:00-19:00, Night: 19:00-7:00)
  const getAutoStyle = () => {
    const hour = new Date().getHours();
    return (hour >= 7 && hour < 19) ? 'light' : 'dark';
  };

  const [mapStyle, setMapStyle] = useState<'dark' | 'light'>(getAutoStyle());

  // Initialize Map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    // Find a fallback center
    const fallbackPoint = visiblePoints.find(p => p.location);
    const fallbackCenter: [number, number] = fallbackPoint 
      ? [fallbackPoint.location!.lng, fallbackPoint.location!.lat] 
      : [2.3522, 48.8566];
    const fallbackZoom = fallbackPoint ? 15 : 13;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: `mapbox://styles/mapbox/${mapStyle}-v11`,
      center: mapFocus ? [mapFocus.lng, mapFocus.lat] : fallbackCenter,
      zoom: mapFocus ? mapFocus.zoomLevel : fallbackZoom,
      pitch: 60,
      bearing: -17.6,
      antialias: true,
      attributionControl: false
    });

    mapRef.current = map;

    const add3DLayer = () => {
      const style = map.getStyle();
      if (!style || !style.layers) return;
      
      const layers = style.layers;
      const labelLayerId = layers?.find(
        (layer) => layer.type === 'symbol' && (layer as any).layout['text-field']
      )?.id;

      if (!map.getLayer('3d-buildings')) {
        map.addLayer(
          {
            'id': '3d-buildings',
            'source': 'composite',
            'source-layer': 'building',
            'filter': ['==', 'extrude', 'true'],
            'type': 'fill-extrusion',
            'minzoom': 15,
            'paint': {
              'fill-extrusion-color': '#aaa',
              'fill-extrusion-height': [
                'interpolate', ['linear'], ['zoom'],
                15, 0, 15.05, ['get', 'height']
              ],
              'fill-extrusion-base': [
                'interpolate', ['linear'], ['zoom'],
                15, 0, 15.05, ['get', 'min_height']
              ],
              'fill-extrusion-opacity': 0.6
            }
          },
          labelLayerId
        );
      }
    };

    /**
     * Surlignage du lieu recherché. `setStyle` détruit toutes les sources et
     * couches : cette fonction doit être rappelée à chaque rechargement de style,
     * sinon le surlignage disparaît dès qu'on change de fond de carte.
     */
    const HIGHLIGHT_SOURCE = 'search-highlight';

    const ensureHighlightLayers = () => {
      if (!map.getSource(HIGHLIGHT_SOURCE)) {
        map.addSource(HIGHLIGHT_SOURCE, {
          type: 'geojson',
          // Le contour courant est réappliqué ici après un changement de style
          data: (highlightDataRef.current || { type: 'FeatureCollection', features: [] }) as any,
        });
      }

      const labelLayerId = map.getStyle()?.layers?.find(
        (layer) => layer.type === 'symbol' && (layer as any).layout?.['text-field']
      )?.id;

      if (!map.getLayer('search-highlight-fill')) {
        map.addLayer({
          id: 'search-highlight-fill',
          type: 'fill',
          source: HIGHLIGHT_SOURCE,
          paint: { 'fill-color': '#6366f1', 'fill-opacity': 0.25 },
        }, labelLayerId);
      }

      if (!map.getLayer('search-highlight-line')) {
        map.addLayer({
          id: 'search-highlight-line',
          type: 'line',
          source: HIGHLIGHT_SOURCE,
          paint: { 'line-color': '#818cf8', 'line-width': 2.5 },
        }, labelLayerId);
      }
    };

    if (map.isStyleLoaded()) {
      add3DLayer();
      ensureHighlightLayers();
    }

    map.on('style.load', () => {
      add3DLayer();
      ensureHighlightLayers();
    });

    map.on('load', () => {
      add3DLayer();
      ensureHighlightLayers();
    });

    if (!isEmbedView) {
      map.on('contextmenu', (e) => {
        e.preventDefault();
        handleLongPressOrRightClickRef.current(e.lngLat);
      });

      let touchTimeout: any = null;
      let touchStartLngLat: mapboxgl.LngLat | null = null;

      map.on('touchstart', (e) => {
        if (e.points.length !== 1) return;
        touchStartLngLat = e.lngLat;
        if (touchTimeout) clearTimeout(touchTimeout);
        touchTimeout = setTimeout(() => {
          if (touchStartLngLat) {
            handleLongPressOrRightClickRef.current(touchStartLngLat);
          }
        }, 700);
      });

      map.on('touchend', () => {
        if (touchTimeout) {
          clearTimeout(touchTimeout);
          touchTimeout = null;
        }
        touchStartLngLat = null;
      });

      map.on('touchmove', () => {
        if (touchTimeout) {
          clearTimeout(touchTimeout);
          touchTimeout = null;
        }
        touchStartLngLat = null;
      });
    }

    return () => {
      map.remove();
      mapRef.current = null;
      // `map.remove()` détruit le marqueur : la référence deviendrait obsolète au remontage
      searchMarkerRef.current = null;
    };
  }, []);

  // Sync Focus
  useEffect(() => {
    if (!mapRef.current || !mapFocus) return;
    // Un lieu recherché est cadré sur son contour par l'effet de surlignage plus bas
    if (mapFocus.placeName || mapFocus.bbox) return;
    const currentCenter = mapRef.current.getCenter();
    const currentZoom = mapRef.current.getZoom();
    
    if (Math.abs(currentCenter.lat - mapFocus.lat) > 0.0001 || 
        Math.abs(currentCenter.lng - mapFocus.lng) > 0.0001 ||
        Math.abs(currentZoom - mapFocus.zoomLevel) > 0.1) {
      mapRef.current.flyTo({
        center: [mapFocus.lng, mapFocus.lat],
        zoom: mapFocus.zoomLevel,
        speed: 1.2,
        curve: 1.42,
        essential: true
      });
    }
  }, [mapFocus]);

  // Surlignage du lieu recherché : son contour réel, ou un cercle de 100 m à défaut
  useEffect(() => {
    const map = mapRef.current;
    if (!map || isEmbedView) return;

    // Seule une recherche renseigne un nom ou un contour : un clic sur un point
    // opérationnel ne doit rien surligner.
    const isSearch = Boolean(mapFocus && (mapFocus.placeName || mapFocus.bbox));
    const source = map.getSource('search-highlight') as mapboxgl.GeoJSONSource | undefined;

    if (!isSearch) {
      highlightDataRef.current = null;
      source?.setData({ type: 'FeatureCollection', features: [] } as any);
      return;
    }

    const geometry = buildHighlight([mapFocus!.lng, mapFocus!.lat], mapFocus!.bbox);
    highlightDataRef.current = geometry;
    source?.setData(geometry as any);

    const [[minLng, minLat], [maxLng, maxLat]] = boundsOf(geometry);
    try {
      map.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
        padding: 70,
        maxZoom: 17,
        duration: 900,
      });
    } catch {
      // Carte pas encore prête : source et contour seront appliqués au chargement
    }
  }, [mapFocus, isEmbedView]);

  // Épingle du lieu recherché
  useEffect(() => {
    const map = mapRef.current;
    if (!map || isEmbedView) return;

    if (!mapFocus?.placeName) {
      searchMarkerRef.current?.remove();
      searchMarkerRef.current = null;
      return;
    }

    if (searchMarkerRef.current) {
      searchMarkerRef.current.setLngLat([mapFocus.lng, mapFocus.lat]);
      return;
    }

    const el = document.createElement('div');
    createRoot(el).render(
      <div className="relative pointer-events-none">
        <div className="absolute -inset-2 bg-indigo-500/40 rounded-full radar-pulsate" />
        <div className="relative z-10 w-6 h-6 rounded-full bg-indigo-500 border-2 border-white shadow-lg flex items-center justify-center">
          <MapPin className="w-3.5 h-3.5 text-white" />
        </div>
      </div>
    );

    searchMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat([mapFocus.lng, mapFocus.lat])
      .addTo(map);
  }, [mapFocus, isEmbedView]);

  // Sync Markers
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    
    // Remove all existing markers to ensure fresh rendering with updated shifts / public incidents
    Object.keys(markersRef.current).forEach(id => {
      markersRef.current[id].remove();
    });
    markersRef.current = {};

    // Add fresh markers
    visiblePoints.filter(p => p.location).forEach(point => {
      const id = point.id;
      const position: [number, number] = [point.location!.lng, point.location!.lat];

      // Create custom marker element
      const el = document.createElement('div');
      el.className = 'custom-marker';
      
      // Render PointIcon into the element
      const root = createRoot(el);
      
      // Calculate status based on current team shifts and active issues
      const now = new Date();
      const teamShifts = _shifts.filter(s => s.teamId === effectiveTeamId && s.pointId === point.id);
      const activeShift = teamShifts.find(s => now >= new Date(s.start) && now <= new Date(s.end));
      
      // Next shift overall (not just at this point) to see if this point is the NEXT one
      const allTeamShifts = _shifts
        .filter(s => s.teamId === effectiveTeamId && new Date(s.end) > now)
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
      
      const nextShift = allTeamShifts.find(s => new Date(s.start) > now);
      const isNextPoint = nextShift?.pointId === point.id;
      const isPastPoint = teamShifts.length > 0 && teamShifts.every(s => now > new Date(s.end));

      // Public Operational Incidents lookup
      const hasPublicIncident = issues.some(i => i.pointId === point.id && !i.resolved && (i.type === 'incident_public' || i.type === 'alert'));

      // Check if there are active artist performances
      const pointShifts = _shifts.filter(s => s.pointId === point.id);
      const hasPerformance = pointShifts.some(s => {
        const assignedTeam = team.find(st => st.id === s.teamId);
        return s.shiftType === 'performance' || assignedTeam?.isArtist === true;
      });

      // ── Embed: check all artists at this point for On Stage / Next ─
      let isOnStageNow = false;
      let isNextUp = false;
      if (isEmbedView && point.isStage) {
        const allPointShifts = _shifts
          .filter(s => s.pointId === point.id)
          .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
        const upcomingShifts = allPointShifts.filter(s => new Date(s.end) > now);
        isOnStageNow = upcomingShifts.some(s => now >= new Date(s.start) && now <= new Date(s.end));
        if (!isOnStageNow) {
          isNextUp = upcomingShifts.length > 0 && upcomingShifts[0] === allPointShifts.find(s => new Date(s.start) > now);
        }
      }

      let markerClass = "";
      let iconColor = undefined;
      let badgeEl: any = null;

      if (point.isStage) {
        markerClass = "flex items-center justify-center transition-all cursor-pointer animate-slow-hop";
        if (isEmbedView && isOnStageNow) {
          markerClass += " text-red-400 scale-150 z-30";
          iconColor = "#ef4444";
          badgeEl = (
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-red-600 text-white text-[7px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap shadow-lg animate-pulse">
              On Stage
            </div>
          );
        } else if (isEmbedView && isNextUp) {
          markerClass += " text-orange-400 scale-125 z-25";
          iconColor = "#f97316";
          badgeEl = (
            <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-orange-600 text-white text-[7px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap shadow-md">
              Next
            </div>
          );
        } else if (hasPublicIncident) {
          markerClass += " text-red-500 z-25 scale-125";
          iconColor = "#ef4444";
        } else if (hasPerformance) {
          markerClass += " text-amber-400 scale-135 z-20";
          iconColor = "#f59e0b";
        } else if (activeShift) {
          markerClass += " text-orange-500 scale-135 z-20";
          iconColor = "#f97316";
        } else if (isNextPoint) {
          markerClass += " text-emerald-400 scale-125 z-10";
          iconColor = "#10b981";
        } else if (isPastPoint) {
          markerClass += " text-slate-500 opacity-40 grayscale";
          iconColor = "#64748b";
        } else {
          markerClass += " hover:scale-135";
          iconColor = embedStageColor || "#f59e0b";
        }
      } else {
        markerClass = "p-1.5 rounded-full shadow-md border flex items-center justify-center transition-all cursor-pointer";
        if (hasPublicIncident) {
          markerClass += " bg-red-500/20 border-red-500 text-red-500 shadow-red-500/20 z-25 scale-110";
          iconColor = "#ef4444";
        } else if (hasPerformance) {
          markerClass += " bg-amber-500/20 border-amber-500 text-amber-400 shadow-amber-500/40 scale-125 z-20 animate-[pulse_1.5s_infinite]";
          iconColor = "#f59e0b";
        } else if (activeShift) {
          markerClass += " bg-orange-500/20 border-orange-500 shadow-orange-500/40 scale-125 z-20";
          iconColor = "#f97316";
        } else if (isNextPoint) {
          markerClass += " bg-emerald-500/20 border-emerald-500 shadow-emerald-500/40 scale-110 z-10 animate-[pulse_3s_infinite]";
          iconColor = "#10b981";
        } else if (isPastPoint) {
          markerClass += " bg-slate-800/50 border-slate-700 opacity-40 grayscale";
          iconColor = "#64748b";
        } else {
          markerClass += " bg-[#14161B] border-indigo-500/30 text-indigo-400 hover:border-indigo-500 hover:scale-125";
          iconColor = embedPointColor || undefined;
        }
      }

      root.render(
        <div className="relative">
          {hasPublicIncident && (
            <div className="absolute -inset-2 bg-red-500/40 rounded-full radar-pulsate pointer-events-none z-0" />
          )}
          {hasPerformance && !hasPublicIncident && (
            <div className="absolute -inset-2 bg-amber-500/40 rounded-full radar-pulsate-gold pointer-events-none z-0" />
          )}
          {/* On Stage / Next badges for embed */}
          {badgeEl}
          {/* Red pulsing ring for On Stage */}
          {isEmbedView && isOnStageNow && (
            <div className="absolute -inset-3 bg-red-500/30 rounded-full animate-pulse pointer-events-none z-0" style={{ animationDuration: '2s' }} />
          )}
          <div className={`relative z-10 ${markerClass}`}>
            <PointIcon name={point.iconName || 'MapPin'} size={activeShift || hasPerformance || isOnStageNow ? 22 : 18} color={iconColor} />
          </div>
        </div>
      );

      const popupNode = document.createElement('div');
      const popupRoot = createRoot(popupNode);
      popupRoot.render(
        <div className="mapbox-popup-content">
          <PointInfoBubble 
            point={point} 
            isTeamView={isTeamView}
            onShowDescription={(p) => setDescriptionModal({ name: p.name, markdown: p.descriptionMarkdown! })} 
          />
        </div>
      );

      const popup = new mapboxgl.Popup({ offset: 25, closeButton: false })
        .setDOMContent(popupNode);

      const marker = new mapboxgl.Marker(el)
        .setLngLat(position)
        .setPopup(popup)
        .addTo(map);
        
      markersRef.current[id] = marker;
    });
  }, [visiblePoints, issues, _shifts, effectiveTeamId, isTeamView]);

  // Handle map click for placement using a dedicated effect to handle selectedPointId closure
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    const clickHandler = (e: mapboxgl.MapMouseEvent) => {
      if (!isSidebarOpen || !selectedPointId) return;
      const { lng, lat } = e.lngLat;
      updatePoint(selectedPointId, {
        location: { lat, lng }
      });
    };

    map.on('click', clickHandler);
    return () => { map.off('click', clickHandler); };
  }, [isSidebarOpen, selectedPointId, updatePoint]);

  // Handle Style & 3D Toggles
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setStyle(`mapbox://styles/mapbox/${mapStyle}-v11`);
  }, [mapStyle]);

  // Periodic check for auto-style (every minute)
  useEffect(() => {
    const timer = setInterval(() => {
      const newStyle = getAutoStyle();
      if (newStyle !== mapStyle) {
        setMapStyle(newStyle);
      }
    }, 60000);
    return () => clearInterval(timer);
  }, [mapStyle]);

  const toggleSidebar = () => {
    setIsSidebarOpen(v => {
      const newValue = !v;
      if (!newValue) setSelectedPointId(null);
      return newValue;
    });
  };

  const toggleFocusDrawer = () => {
    setIsFocusDrawerOpen(!isFocusDrawerOpen);
  };

  return (
    <div className="flex h-full overflow-hidden relative font-sans">
      <style>{`
        @keyframes slow-hop {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        .animate-slow-hop {
          animation: slow-hop 4s ease-in-out infinite;
        }
        .mapboxgl-popup-content {
          background: #14161B !important;
          color: white !important;
          border: 1px solid #2D3139 !important;
          border-radius: 16px !important;
          padding: 0 !important;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5) !important;
          width: 20rem !important;
          max-width: 20rem !important;
        }
        .mapboxgl-popup-tip {
          border-top-color: #2D3139 !important;
        }
        .mapbox-popup-content {
          width: 100%;
        }
        .mapboxgl-ctrl-logo, .mapboxgl-ctrl-attrib {
          display: none !important;
        }
      `}</style>

      {/* Sidebar — hidden in embed mode */}
      {currentUser.role !== 'staff' && !isEmbedView && (
        <div className={`flex-none bg-[#14161B]/95 backdrop-blur-xl border-r border-[#2D3139] flex flex-col transition-all duration-300 ease-in-out z-30 shadow-2xl overflow-hidden
          ${isSidebarOpen ? 'w-80 translate-x-0' : 'w-0 -translate-x-full'}`}>
          <div className="p-5 border-b border-[#2D3139]/50 bg-gradient-to-b from-[#1A1D23] to-transparent flex flex-col gap-3 whitespace-nowrap">
            <div>
              <h3 className="font-bold text-white text-lg tracking-wide">Placer un Point</h3>
              <p className="text-xs text-slate-400 mt-1 whitespace-normal leading-relaxed">Sélectionnez un point puis cliquez sur la carte.</p>
            </div>

            {/* Search Bar for OP Points */}
            <div className="relative w-full">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="w-4 h-4 text-slate-400" />
              </span>
              <input
                type="text"
                value={pointSearchQuery}
                onChange={e => setPointSearchQuery(e.target.value)}
                placeholder="Rechercher un point..."
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl pl-9 pr-8 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all font-medium"
              />
              {pointSearchQuery && (
                <button
                  onClick={() => setPointSearchQuery('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-white"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
            {(() => {
              const sidebarFilteredPoints = visiblePoints.filter(p =>
                p.name.toLowerCase().includes(pointSearchQuery.toLowerCase())
              );
              return (
                <>
                  {sidebarFilteredPoints.map(p => (
                    <button
                      key={p.id}
                      onClick={() => setSelectedPointId(p.id)}
                      className={`w-full text-left p-4 rounded-xl text-sm transition-all duration-300 whitespace-nowrap overflow-hidden text-ellipsis shadow-sm hover:-translate-y-0.5 ${
                        selectedPointId === p.id 
                          ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/40 shadow-indigo-500/10' 
                          : p.isStage
                            ? 'bg-amber-500/5 text-slate-300 hover:bg-amber-500/10 border border-amber-500/30 hover:border-amber-500/50'
                            : 'text-slate-300 hover:bg-[#1A1D23] border border-[#2D3139]/50 hover:border-[#3D414D]'
                      }`}
                    >
                      <div className="font-bold truncate text-[13px] flex items-center justify-between">
                        <span>{p.name}</span>
                        {p.isStage && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 uppercase tracking-widest font-black flex items-center gap-0.5">
                            <Music className="w-2.5 h-2.5" /> Scène
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] mt-1.5 uppercase tracking-wider font-semibold flex items-center gap-1.5">
                        {p.location ? (
                          <><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> <span className="text-emerald-400">Placé</span></>
                        ) : (
                          <><span className="w-1.5 h-1.5 rounded-full bg-slate-600"></span> <span className="text-slate-500">Non Placé</span></>
                        )}
                      </div>
                    </button>
                  ))}
                  {sidebarFilteredPoints.length === 0 && (
                    <div className="text-center py-8 text-xs text-slate-500 italic">
                      Aucun point trouvé
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Button to toggle sidebar — hidden in embed mode */}
      {currentUser.role !== 'staff' && !isEmbedView && (
        <button 
          onClick={toggleSidebar}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-40 bg-[#1A1D23]/90 backdrop-blur border border-l-0 border-[#2D3139] text-slate-300 rounded-r-xl p-2.5 shadow-xl hover:text-white hover:bg-indigo-600/20 hover:border-indigo-500/50 transition-all duration-300"
          style={{ transform: `translateY(-50%) translateX(${isSidebarOpen ? '20rem' : '0'})` }}
        >
          {isSidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
      )}

      {/* Right-side Focus Drawer Button — hidden in embed mode */}
      {currentUser.role !== 'staff' && !isEmbedView && (
        <button 
          onClick={toggleFocusDrawer}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-40 bg-[#1A1D23]/90 backdrop-blur border border-r-0 border-[#2D3139] text-slate-300 rounded-l-xl p-2.5 shadow-xl hover:text-white hover:bg-indigo-600/20 hover:border-indigo-500/50 transition-all duration-300"
          style={{ transform: `translateY(-50%) translateX(${isFocusDrawerOpen ? '-18rem' : '0'})` }}
        >
          {isFocusDrawerOpen ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
        </button>
      )}

      {/* Right-side Focus Drawer — hidden in embed mode */}
      {currentUser.role !== 'staff' && !isEmbedView && (
        <div className={`fixed inset-y-0 right-0 z-30 w-72 bg-[#14161B]/95 backdrop-blur-xl border-l border-[#2D3139] flex flex-col transition-all duration-300 ease-in-out shadow-2xl overflow-hidden
          ${isFocusDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="p-5 border-b border-[#2D3139]/50 bg-gradient-to-b from-[#1A1D23] to-transparent whitespace-nowrap">
            <h3 className="font-bold text-white text-lg tracking-wide">Points Focus</h3>
            <p className="text-xs text-slate-400 mt-1">Accès rapide aux points stratégiques.</p>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
            {visiblePoints.filter(p => p.isMapFocus && p.location).map(p => (
              <button
                key={p.id}
                onClick={() => {
                  setMapFocus({ lat: p.location!.lat, lng: p.location!.lng, zoomLevel: 18 });
                }}
                className="w-full bg-[#1A1D23] hover:bg-indigo-500/20 text-slate-300 hover:text-indigo-300 border border-[#2D3139] hover:border-indigo-500/50 p-4 rounded-xl text-sm font-bold transition-all shadow-sm text-left"
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 relative w-full h-full overflow-hidden">
        <div ref={mapContainerRef} className="w-full h-full" />
        
        {/* Retractable Map Search Bar */}
        {!isTeam && (
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-[40] w-80 max-w-[90vw] flex flex-col items-center">
            <div className={`w-full bg-[#14161B]/95 backdrop-blur-xl border-x border-b border-[#2D3139] rounded-b-2xl px-4 shadow-2xl transition-all duration-500 ease-in-out ${
              isSearchOpen ? 'max-h-[400px] opacity-100 translate-y-0 overflow-visible pt-2 pb-4' : 'max-h-0 pt-0 pb-0 opacity-0 -translate-y-4 border-none pointer-events-none overflow-hidden'
            }`}>
              <div className="text-left mb-2 flex items-center justify-between">
                <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Rechercher un lieu</span>
                <button 
                  onClick={() => setIsSearchOpen(false)} 
                  className="text-slate-500 hover:text-white p-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              {hasGoogleKey ? (
                <GoogleAutocompleteSearch onLocationSelected={(lat, lng, details) => {
                  setMapFocus({ lat, lng, zoomLevel: 18, placeName: details?.name, bbox: details?.bbox });
                  setIsSearchOpen(false);
                }} />
              ) : (
                <MapboxGeocodingSearch onLocationSelected={(lat, lng, details) => {
                  setMapFocus({ lat, lng, zoomLevel: 18, placeName: details?.name, bbox: details?.bbox });
                  setIsSearchOpen(false);
                }} />
              )}
            </div>
            {/* Loop / Toggle Handle Button */}
            <div className="flex justify-center -mt-px">
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className={`bg-[#14161B]/95 backdrop-blur-xl border-x border-b border-[#2D3139] px-5 py-2 rounded-b-2xl text-slate-400 hover:text-white transition-all flex items-center gap-2 shadow-2xl active:pt-2.5 hover:border-indigo-500/50 ${
                  isSearchOpen ? 'border-t border-[#2D3139]/40' : ''
                }`}
                title="Rechercher un lieu"
              >
                <Search className={`w-3.5 h-3.5 transition-transform duration-500 ${isSearchOpen ? 'rotate-90 text-indigo-400 font-bold' : 'text-slate-400'}`} />
                <span className="text-[9px] font-extrabold tracking-widest uppercase">
                  {isSearchOpen ? "Fermer" : "Rechercher"}
                </span>
              </button>
            </div>

            {/* Lieu actuellement surligné sur la carte */}
            {mapFocus?.placeName && (
              <div className="mt-2 w-full flex items-center gap-2 bg-indigo-500/15 border border-indigo-500/30 rounded-xl px-3 py-2 shadow-lg">
                <MapPin className="w-3 h-3 text-indigo-300 flex-none" />
                <span className="text-[10px] text-indigo-200 truncate flex-1" title={mapFocus.placeName}>
                  {mapFocus.placeName}
                </span>
                <button
                  onClick={() => setMapFocus(null)}
                  className="text-indigo-300 hover:text-white p-0.5 flex-none"
                  title="Effacer le surlignage"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        )}
        
        {selectedPointId && (
           <div className="absolute top-20 left-4 bg-[#14161B] border border-indigo-500 text-indigo-300 px-4 py-2 rounded-lg shadow-lg text-sm font-medium z-10 pointer-events-none">
             Cliquez sur la carte pour placer "{visiblePoints.find(p => p.id === selectedPointId)?.name}"
           </div>
        )}

        {/* Description Modal */}
        {descriptionModal && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm z-[500] flex items-center justify-center p-4" onClick={() => setDescriptionModal(null)}>
            <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
              <div className="p-5 border-b border-[#2D3139] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <InfoIcon className="w-4 h-4 text-emerald-400" />
                  <h3 className="font-bold text-white">{descriptionModal.name}</h3>
                </div>
                <button onClick={() => setDescriptionModal(null)} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#2D3139]"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 custom-scrollbar prose prose-invert prose-sm max-w-none">
                <Markdown remarkPlugins={[remarkGfm, remarkBreaks]}>{descriptionModal.markdown}</Markdown>
              </div>
              <div className="p-4 border-t border-[#2D3139]">
                <button onClick={() => setDescriptionModal(null)} className="w-full py-2.5 bg-[#1A1D23] hover:bg-[#2D3139] text-white font-medium rounded-xl text-sm transition-colors">Fermer</button>
              </div>
            </div>
          </div>
        )}

        {/* Create Point Modal */}
        {createPointModal && (
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md z-[500] flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl w-full max-w-lg shadow-2xl flex flex-col my-8" onClick={e => e.stopPropagation()}>
              <div className="p-5 border-b border-[#2D3139] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-indigo-400" />
                  <h3 className="font-bold text-white text-lg">Créer un Point Opérationnel</h3>
                </div>
                <button onClick={() => setCreatePointModal(null)} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#2D3139]">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
                {/* Lat/Lng display */}
                <div className="p-3 bg-[#0A0B0E] rounded-xl border border-[#2D3139]/50 flex items-center justify-between text-xs text-slate-400">
                  <span>Coordonnées :</span>
                  <span className="font-mono text-indigo-400 font-bold">{createPointModal.lat.toFixed(6)}, {createPointModal.lng.toFixed(6)}</span>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Nom du Point</label>
                  <input
                    type="text"
                    value={newPointForm.name}
                    onChange={e => setNewPointForm({ ...newPointForm, name: e.target.value })}
                    placeholder="Ex: Bar Principal, Scène Electro..."
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] text-white text-sm px-4 py-2.5 rounded-xl focus:outline-none focus:border-indigo-500/50 transition-all font-semibold"
                  />
                </div>

                {/* Category Selection */}
                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Catégorie</label>
                  {currentUser.role === 'admin' ? (
                    <select
                      value={newPointForm.categoryId}
                      onChange={e => {
                        const catId = e.target.value;
                        const catObj = categories.find(c => c.id === catId);
                        setNewPointForm({ 
                          ...newPointForm, 
                          categoryId: catId,
                          isStage: catObj?.isArtistCategory === true
                        });
                      }}
                      className="w-full bg-[#0A0B0E] border border-[#2D3139] text-white text-sm px-4 py-2.5 rounded-xl focus:outline-none focus:border-indigo-500/50 transition-all font-semibold"
                    >
                      {categories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="w-full bg-[#0A0B0E]/60 border border-[#2D3139]/40 text-slate-400 text-sm px-4 py-2.5 rounded-xl font-semibold">
                      {categories.find(c => c.id === newPointForm.categoryId)?.name || 'Votre Catégorie'}
                    </div>
                  )}
                </div>

                {/* Is Stage checkbox */}
                {canSeeStageOption && (
                  <div className="pt-1">
                    <label className="flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer bg-[#0A0B0E] border-[#2D3139] hover:border-slate-700">
                      <input
                        type="checkbox"
                        checked={newPointForm.isStage}
                        onChange={e => setNewPointForm({ ...newPointForm, isStage: e.target.checked })}
                        className="rounded border-[#2D3139] bg-[#0A0B0E] text-indigo-500 focus:ring-indigo-500/30"
                      />
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Scène / Stage</span>
                        <span className="text-[9px] text-slate-500">Cochez cette case pour y affecter uniquement des artistes.</span>
                      </div>
                    </label>
                  </div>
                )}

                {/* Hours row */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Date</label>
                    <select
                      value={newPointForm.date}
                      onChange={e => setNewPointForm({ ...newPointForm, date: e.target.value })}
                      className="w-full bg-[#0A0B0E] border border-[#2D3139] text-white text-xs px-3 py-2.5 rounded-xl focus:outline-none focus:border-indigo-500/50 transition-all font-semibold"
                    >
                      {festivalDates.length > 0 ? (
                        festivalDates.map((d: string) => <option key={d} value={d}>{d}</option>)
                      ) : (
                        <option value={new Date().toISOString().split('T')[0]}>{new Date().toISOString().split('T')[0]}</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Ouverture</label>
                    <input
                      type="time"
                      value={newPointForm.startTime}
                      onChange={e => setNewPointForm({ ...newPointForm, startTime: e.target.value })}
                      className="w-full bg-[#0A0B0E] border border-[#2D3139] text-white text-xs px-3 py-2.5 rounded-xl focus:outline-none focus:border-indigo-500/50 transition-all font-semibold font-mono text-center"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Fermeture</label>
                    <input
                      type="time"
                      value={newPointForm.endTime}
                      onChange={e => setNewPointForm({ ...newPointForm, endTime: e.target.value })}
                      className="w-full bg-[#0A0B0E] border border-[#2D3139] text-white text-xs px-3 py-2.5 rounded-xl focus:outline-none focus:border-indigo-500/50 transition-all font-semibold font-mono text-center"
                    />
                  </div>
                </div>

                {/* Team Assignment Section */}
                <div className="pt-2 border-t border-[#2D3139]/40">
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-indigo-400" />
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                      {newPointForm.isStage ? "Artistes à affecter" : "Team à affecter"}
                    </span>
                  </div>

                  {/* Dynamic checklist of candidates */}
                  <div className="space-y-2.5 max-h-52 overflow-y-auto custom-scrollbar pr-1">
                    {(newPointForm.isStage ? team.filter(s => s.isArtist) : team.filter(s => !s.isArtist)).length === 0 ? (
                      <div className="text-center py-4 text-xs text-slate-500 italic">
                        {newPointForm.isStage ? "Aucun artiste configuré" : "Aucun team disponible"}
                      </div>
                    ) : (
                      (newPointForm.isStage ? team.filter(s => s.isArtist) : team.filter(s => !s.isArtist)).map(member => {
                        const isAssigned = newPointForm.assignedTeam.some(s => s.teamId === member.id);
                        const assignedData = newPointForm.assignedTeam.find(s => s.teamId === member.id);
                        
                        return (
                          <div key={member.id} className={`p-3 rounded-xl border transition-all ${isAssigned ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-[#0A0B0E] border-[#2D3139]'}`}>
                            <div className="flex items-center justify-between gap-3">
                              <label className="flex items-center gap-2.5 cursor-pointer min-w-0 flex-1">
                                <input
                                  type="checkbox"
                                  checked={isAssigned}
                                  onChange={e => {
                                    if (e.target.checked) {
                                      setNewPointForm({
                                        ...newPointForm,
                                        assignedTeam: [...newPointForm.assignedTeam, { teamId: member.id, start: newPointForm.startTime, end: newPointForm.endTime }]
                                      });
                                    } else {
                                      setNewPointForm({
                                        ...newPointForm,
                                        assignedTeam: newPointForm.assignedTeam.filter(s => s.teamId !== member.id)
                                      });
                                    }
                                  }}
                                  className="rounded border-[#2D3139] bg-[#0A0B0E] text-indigo-500 focus:ring-indigo-500/30"
                                />
                                <span className="text-xs font-bold text-white truncate">{member.name}</span>
                              </label>
                              
                              {/* Display specific times if checked */}
                              {isAssigned && assignedData && (
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <input
                                    type="time"
                                    value={assignedData.start}
                                    onChange={e => {
                                      setNewPointForm({
                                        ...newPointForm,
                                        assignedTeam: newPointForm.assignedTeam.map(s => s.teamId === member.id ? { ...s, start: e.target.value } : s)
                                      });
                                    }}
                                    className="bg-[#0A0B0E] border border-[#2D3139] text-white text-[10px] px-1.5 py-1 rounded-lg font-mono text-center w-16"
                                  />
                                  <span className="text-[10px] text-slate-500 font-bold">à</span>
                                  <input
                                    type="time"
                                    value={assignedData.end}
                                    onChange={e => {
                                      setNewPointForm({
                                        ...newPointForm,
                                        assignedTeam: newPointForm.assignedTeam.map(s => s.teamId === member.id ? { ...s, end: e.target.value } : s)
                                      });
                                    }}
                                    className="bg-[#0A0B0E] border border-[#2D3139] text-white text-[10px] px-1.5 py-1 rounded-lg font-mono text-center w-16"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-4 border-t border-[#2D3139] flex gap-3">
                <button
                  onClick={() => setCreatePointModal(null)}
                  className="flex-1 py-2.5 border border-[#2D3139] hover:bg-[#1A1D23] text-white rounded-xl transition-all text-xs font-bold"
                >
                  Annuler
                </button>
                <button
                  disabled={!newPointForm.name.trim()}
                  onClick={() => {
                    const newPointId = crypto.randomUUID();
                    // 1. Add Point
                    addPoint({
                      id: newPointId,
                      name: newPointForm.name,
                      categoryId: newPointForm.categoryId,
                      requiredTeam: 1,
                      openingHours: [
                        {
                          start: `${newPointForm.date}T${newPointForm.startTime}`,
                          end: `${newPointForm.date}T${newPointForm.endTime}`,
                          requiredTeam: 1
                        }
                      ],
                      location: { lat: createPointModal.lat, lng: createPointModal.lng },
                      iconName: newPointForm.isStage ? 'Music' : 'MapPin',
                      isStage: newPointForm.isStage,
                      descriptionMarkdown: '',
                      isRestricted: false
                    } as any);

                    // 2. Add Shifts for Assigned Team
                    newPointForm.assignedTeam.forEach(as => {
                      addShift({
                        pointId: newPointId,
                        teamId: as.teamId,
                        start: `${newPointForm.date}T${as.start}`,
                        end: `${newPointForm.date}T${as.end}`,
                        description: newPointForm.isStage ? 'Soundcheck/Performance' : 'Service',
                        shiftType: newPointForm.isStage ? 'performance' : 'standard',
                        agreementStatus: 'approved'
                      });
                    });

                    setCreatePointModal(null);
                  }}
                  className="flex-1 py-2.5 disabled:opacity-50 text-white rounded-xl font-bold transition-all shadow-lg bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 shadow-indigo-500/20 text-xs"
                >
                  Créer le Point
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Report Issue Modal */}
        {reportIssueModal && (
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md z-[500] flex items-center justify-center p-4">
            <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl w-full max-w-md shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="p-5 border-b border-[#2D3139] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 animate-pulse" />
                  <h3 className="font-bold text-white text-lg">Signaler un Problème</h3>
                </div>
                <button onClick={() => setReportIssueModal(null)} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#2D3139]">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="p-3 bg-[#0A0B0E] rounded-xl border border-[#2D3139]/50 flex items-center justify-between text-xs text-slate-400">
                  <span>Coordonnées :</span>
                  <span className="font-mono text-red-400 font-bold">{reportIssueModal.lat.toFixed(6)}, {reportIssueModal.lng.toFixed(6)}</span>
                </div>

                {/* Point Dropdown */}
                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Point Opérationnel Lié</label>
                  <select
                    value={issueForm.pointId}
                    onChange={e => setIssueForm({ ...issueForm, pointId: e.target.value })}
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] text-white text-sm px-4 py-2.5 rounded-xl focus:outline-none focus:border-indigo-500/50 transition-all font-semibold"
                  >
                    {points.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Description de l'incident</label>
                  <textarea
                    rows={4}
                    value={issueForm.message}
                    onChange={e => setIssueForm({ ...issueForm, message: e.target.value })}
                    placeholder="Décrivez précisément le problème rencontré à cet endroit..."
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] text-white text-sm px-4 py-3 rounded-xl focus:outline-none focus:border-indigo-500/50 transition-all font-semibold resize-none"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-4 border-t border-[#2D3139] flex gap-3">
                <button
                  onClick={() => setReportIssueModal(null)}
                  className="flex-1 py-2.5 border border-[#2D3139] hover:bg-[#1A1D23] text-white rounded-xl transition-all text-xs font-bold"
                >
                  Annuler
                </button>
                <button
                  disabled={!issueForm.message.trim()}
                  onClick={() => {
                    const effectiveTeamId = impersonatedTeamId || currentUser.teamId;
                    addIssue({
                      pointId: issueForm.pointId,
                      teamId: effectiveTeamId || '',
                      teamName: team.find(s => s.id === effectiveTeamId)?.name || currentUser.role,
                      message: issueForm.message,
                      type: 'incident_public'
                    });
                    setReportIssueModal(null);
                  }}
                  className="flex-1 py-2.5 disabled:opacity-50 text-white rounded-xl font-bold transition-all shadow-lg bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 shadow-red-500/20 text-xs"
                >
                  Signaler l'Incident
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Contexte renvoyé avec la position : nom du lieu et contour, s'il en a un. */
type SearchDetails = { name?: string; bbox?: Bbox };

interface SearchProps {
  onLocationSelected: (lat: number, lng: number, details?: SearchDetails) => void;
  placeholder?: string;
  googleError?: string;
}

function GoogleAutocompleteSearch({ onLocationSelected }: SearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [useFallback, setUseFallback] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  
  const apiStatus = useApiLoadingStatus();
  const placesLib = useMapsLibrary('places');
  const geocodingLib = useMapsLibrary('geocoding');
  
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  // Fallback if Google Maps fails to load
  useEffect(() => {
    if (apiStatus === APILoadingStatus.FAILED) {
      console.warn("Google Maps API failed to load. Falling back to Mapbox Geocoding search.");
      setGoogleError("Le script Google Maps n'a pas pu être chargé (vérifiez la clé ou les restrictions).");
      setUseFallback(true);
    }
  }, [apiStatus]);

  // Loading timeout (2.5s) to guarantee Mapbox fallback if Google Places gets stuck/blocked
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!placesLib || !geocodingLib) {
        console.warn("Google Places/Geocoding library loading timed out. Falling back to Mapbox.");
        setGoogleError("Délai d'attente dépassé pour les librairies Google Places/Geocoding.");
        setUseFallback(true);
      }
    }, 2500);
    return () => clearTimeout(timer);
  }, [placesLib, geocodingLib]);

  useEffect(() => {
    if (placesLib) {
      autocompleteServiceRef.current = new placesLib.AutocompleteService();
    }
  }, [placesLib]);

  useEffect(() => {
    if (geocodingLib) {
      geocoderRef.current = new geocodingLib.Geocoder();
    }
  }, [geocodingLib]);

  const fallbackToMapboxGeocoding = async (query: string) => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&limit=1`
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} (${response.statusText || 'Forbidden'})`);
      }
      const data = await response.json();
      if (data.features && data.features[0]) {
        const feature = data.features[0];
        const [lng, lat] = feature.center;
        const bbox: Bbox | undefined =
          Array.isArray(feature.bbox) && feature.bbox.length === 4 ? feature.bbox as Bbox : undefined;
        onLocationSelected(lat, lng, { name: feature.place_name, bbox });
        setSearchQuery(feature.place_name);
      }
    } catch (e: any) {
      console.error('Fallback geocoding error:', e);
      setGoogleError(prev => `${prev ? prev + ' + ' : ''}Géocodage Mapbox échoué: ${e.message || 'Erreur'}`);
    }
  };

  if (useFallback) {
    return (
      <MapboxGeocodingSearch 
        onLocationSelected={onLocationSelected} 
        placeholder="Rechercher un lieu (Mapbox - Google non disponible)..." 
        googleError={googleError || "Service Google indisponible"}
      />
    );
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    if (autocompleteServiceRef.current) {
      try {
        autocompleteServiceRef.current.getPlacePredictions(
          { input: query },
          (predictions, status) => {
            if (status === 'OK' && predictions) {
              setSearchResults(predictions);
              setGoogleError(null);
            } else {
              setSearchResults([]);
              // Fallback to Mapbox search if Google Places API returns an error status (like REQUEST_DENIED)
              if (status !== 'ZERO_RESULTS') {
                console.warn(`Google Places API returned status: ${status}. Falling back to Mapbox Geocoding search.`);
                setGoogleError(`Erreur API Google Places: ${status}`);
                setUseFallback(true);
              }
            }
          }
        );
      } catch (err: any) {
        console.error("Error in getPlacePredictions, falling back to Mapbox:", err);
        setGoogleError(`Exception Google: ${err.message || err}`);
        setUseFallback(true);
      }
    } else {
      if (apiStatus === APILoadingStatus.LOADED && !placesLib) {
        console.warn("Google Maps loaded but Places library is missing. Falling back to Mapbox.");
        setGoogleError("Librairie Places manquante après chargement.");
        setUseFallback(true);
      }
    }
  };

  const selectSearchResult = (result: google.maps.places.AutocompletePrediction) => {
    if (geocoderRef.current) {
      geocoderRef.current.geocode({ placeId: result.place_id }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const lat = results[0].geometry.location.lat();
          const lng = results[0].geometry.location.lng();
          // Le viewport du résultat est son contour : c'est lui qui permet de le situer sur la carte
          const sw = results[0].geometry.viewport?.getSouthWest();
          const ne = results[0].geometry.viewport?.getNorthEast();
          const bbox: Bbox | undefined = sw && ne
            ? [sw.lng(), sw.lat(), ne.lng(), ne.lat()]
            : undefined;
          onLocationSelected(lat, lng, { name: result.description, bbox });
          setSearchResults([]);
          setSearchQuery(result.description);
        } else {
          console.warn(`Google Geocoding failed with status: ${status}. Trying Mapbox Geocoding fallback.`);
          setGoogleError(`Erreur géocodage Google: ${status}`);
          fallbackToMapboxGeocoding(result.description);
        }
      });
    } else {
      setGoogleError("Géocodeur Google non disponible pour la sélection.");
      fallbackToMapboxGeocoding(result.description);
    }
  };

  const isLoading = apiStatus === APILoadingStatus.LOADING || !placesLib || !geocodingLib;

  return (
    <div className="mt-1.5 relative">
      <input
        type="text"
        disabled={isLoading}
        value={isLoading ? '' : searchQuery}
        onChange={(e) => handleSearch(e.target.value)}
        placeholder={isLoading ? "Chargement de la recherche Google..." : "Rechercher un lieu (Google)..."}
        className={`w-full bg-[#0A0B0E] border text-white text-sm px-4 py-2.5 rounded-xl focus:outline-none transition-all ${
          isLoading ? 'border-[#2D3139]/40 opacity-60 cursor-not-allowed' : 'border-[#2D3139] focus:border-indigo-500/50'
        }`}
      />
      {searchResults.length > 0 && (
        <div className="absolute left-0 right-0 mt-2 bg-[#1A1D23] border border-[#2D3139] rounded-xl shadow-2xl z-50 max-h-60 overflow-y-auto flex flex-col whitespace-normal">
          {searchResults.map((result, i) => (
            <button
              key={i}
              onClick={() => selectSearchResult(result)}
              className="w-full text-left px-4 py-3 text-xs text-slate-300 hover:bg-indigo-500/20 hover:text-white border-b border-[#2D3139]/50 last:border-0 transition-colors"
            >
              {result.description}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MapboxGeocodingSearch({ onLocationSelected, placeholder, googleError }: SearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [mapboxError, setMapboxError] = useState<string | null>(null);

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&limit=5`
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} (${response.statusText || 'Forbidden'})`);
      }
      const data = await response.json();
      setSearchResults(data.features || []);
      setMapboxError(null);
    } catch (e: any) {
      console.error('Geocoding error:', e);
      setMapboxError(e.message || 'Erreur de recherche');
    }
  };

  const selectSearchResult = (result: any) => {
    const [lng, lat] = result.center;
    // `bbox` n'est renvoyé que pour les lieux ayant un contour (ville, quartier, adresse…) ;
    // pour un bâtiment précis il est absent, et la carte dessinera un cercle à la place.
    const bbox: Bbox | undefined =
      Array.isArray(result.bbox) && result.bbox.length === 4 ? result.bbox as Bbox : undefined;
    onLocationSelected(lat, lng, { name: result.place_name, bbox });
    setSearchResults([]);
    setSearchQuery(result.place_name);
  };

  return (
    <div className="mt-1.5 relative">
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => handleSearch(e.target.value)}
        placeholder={placeholder || "Rechercher un lieu (Mapbox)..."}
        className="w-full bg-[#0A0B0E] border border-[#2D3139] text-white text-sm px-4 py-2.5 rounded-xl focus:outline-none focus:border-indigo-500/50 transition-all"
      />
      {googleError && (
        <div className="mt-1.5 text-[9px] text-amber-400 font-semibold px-2.5 py-1 bg-amber-500/5 border border-amber-500/10 rounded-lg flex items-center gap-1.5 leading-tight">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
          <span>Google non dispo : {googleError}</span>
        </div>
      )}
      {mapboxError && (
        <div className="mt-1 text-[9px] text-rose-400 font-semibold px-2.5 py-1 bg-rose-500/5 border border-rose-500/10 rounded-lg flex items-center gap-1.5 leading-tight">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"></span>
          <span>Mapbox non dispo : {mapboxError}</span>
        </div>
      )}
      {searchResults.length > 0 && (
        <div className="absolute left-0 right-0 mt-2 bg-[#1A1D23] border border-[#2D3139] rounded-xl shadow-2xl z-50 max-h-60 overflow-y-auto flex flex-col whitespace-normal">
          {searchResults.map((result, i) => (
            <button
              key={i}
              onClick={() => selectSearchResult(result)}
              className="w-full text-left px-4 py-3 text-xs text-slate-300 hover:bg-indigo-500/20 hover:text-white border-b border-[#2D3139]/50 last:border-0 transition-colors"
            >
              {result.place_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
